import { Link as LinkR } from "react-router-dom";
import styled, { css } from "styled-components";

/**
 * Colours here are CSS custom properties from `global/theme/tokens.css`, not
 * `${({ theme }) => ...}` lookups (D-034). The navbar is one of the three
 * surfaces migrated in phase 1 of the theme work, and the token has to come
 * from the cascade rather than from the styled-components ThemeProvider
 * because that provider is pinned to `darkTheme` until every section has
 * moved — a `theme.*` lookup here could not follow `<html data-theme>`.
 *
 * Every dark value is what this file already rendered, with one deliberate
 * exception: the accent used AS TEXT is now `--accent-interactive-text`
 * (#9a6bec) rather than `theme.primary` (#854ce6), because #854ce6 on the
 * #191924 chrome is 3.46:1 and fails WCAG AA. The fill it turns into on hover
 * is still #854ce6, which carries white at 5.03:1. See D-034.
 */

/**
 * Shared by every navigation link, anchor or route alike.
 *
 * Extracted when the /stories route landed: a route link must be a
 * react-router <Link>, not an <a>, but it has to look identical to the section
 * anchors beside it. One source of truth beats two definitions that drift.
 * The declarations below are byte-for-byte what NavLink and MobileLink each
 * carried before — no visual change.
 */
const navLinkStyles = css`
	color: var(--text-primary);
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease-in-out;
	text-decoration: none;
	&:hover {
		color: var(--accent-interactive-text);
	}

	&.active {
		border-bottom: 2px solid var(--accent-interactive-text);
	}
`;

interface MobileMenuProps {
	isOpen: boolean;
}

export const Nav = styled.div`
	background-color: var(--bg-elevated);
	/* Added in D-035 and inert in dark, where the token is a transparent
	   zero-offset shadow and paints nothing. The navbar is sticky, so on
	   porcelain it needs to seat itself above the content scrolling under it;
	   dark already separates the two by tone. */
	box-shadow: var(--shadow-chrome);
	min-height: 80px;
	height: auto;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1rem;
	position: sticky;
	top: 0;
	z-index: 10;
`;
export const NavbarContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	min-height: 60px;
	height: auto;
	z-index: 1;
	width: 100%;
	padding: 8px 24px;
	max-width: 1200px;
`;

export const NavLogo = styled(LinkR)`
	flex: 0 0 auto;
	/* WIDTH IS AUTO AGAIN, and NavLogoMark below carries the clamp instead
	   (D-058). The old arrangement sized this box and then sized the image as
	   35% OF IT, so the wordmark's offset had to be tuned against a width that
	   moved — and it was tuned once, in px, so it could only ever be right at
	   one width. Sizing the image directly removes the dependency rather than
	   re-tuning it, and shrink-to-fit here also returns ~30px to the nav track
	   at 1280 (D-059). */
	min-width: 0;
	padding: 0 6px;
	display: flex;
	justify-content: start;
	align-items: center;
	text-decoration: none;
	@media (max-width: 640px) {
		padding: 0 0px;
	}
`;
/**
 * The monogram. Sized here rather than as a percentage of the link, because
 * `sm_final_logo.png` is 500x500 with the mark occupying x[146..353] and
 * y[128..336] — **29.2% of the frame is transparent on each side**, measured
 * off the asset's own alpha channel rather than assumed.
 *
 * That padding is what the wordmark's old `margin-left: -1.7rem` was closing.
 * A fixed -27.2px pull against an image that scaled with the link could only
 * be correct at one width, and it was correct at none of the ones tested: it
 * covered 19% of the visible mark at 1280 and **78% of it at 768**, which is
 * the half-circle the design audit found (D-058).
 *
 * Nothing compensates for the padding now — the padding IS the gap, and
 * because it is a fraction of the image it scales by itself: 16.3px of
 * clearance at 390, 22.1px at 1280. The clamp reproduces the previous rendered
 * sizes (42px at <=640, 62px at >=1280), so the mark itself is the size it
 * always was.
 */
export const NavLogoMark = styled.img`
	flex: 0 0 auto;
	width: clamp(42px, 4.85vw, 62px);
	height: auto;
`;

export const Span = styled.div`
	padding: 0 4px;
	font-weight: bold;
	font-size: 18px;
`;
export const NavItems = styled.ul`
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	/* "wrap" is kept as the failure mode, not as the layout (D-018 chose it so
	   an over-long set wraps rather than overflowing a clipping ancestor). It
	   should now never engage, and navrow.mjs asserts that it does not at 50
	   widths from 769 to 2560 in both themes. If it ever wraps again,
	   something grew — and wrapping is still the right way to find out. */
	flex-wrap: wrap;
	row-gap: 8px;
	/* 1.2vw, down from 1.6vw (D-059). The gap is the third lever after the CTA
	   label and the logo, and the slope is what decides the narrowest width
	   that fits: at 1.6vw the eight labels need 679px of a 658px track at
	   1025, and at 1.2vw they need 650px. The 12px floor is untouched. */
	column-gap: clamp(12px, 1.2vw, 28px);
	padding: 8px 6px;
	list-style: none;
	margin: 0;

	/* 768 AGAIN, and D-059's own arithmetic is why that is now honest (D-068).
	   That decision moved this breakpoint UP to 1024 because eight flat labels
	   at 769px needed 660px of a 721px content box — leaving 61px for the
	   logo, the theme toggle and the call to action together, which is not a
	   tight row, it is a three-row navbar. The set is no longer eight labels;
	   it is FOUR: "About" plus three group triggers. The tablet band gets real
	   navigation back rather than a hamburger, and the number that changed is
	   the one in the numerator. Measured by navrow.mjs at 50 widths from 769 to
	   2560, in both themes and on three routes: one row at every one of them,
	   and the navbar is 80px rather than the 82px two rows cost. */
	@media screen and (max-width: 768px) {
		display: none;
	}
`;

/** Section anchors within the current page. */
export const NavLink = styled.a`
	${navLinkStyles}
`;

/** Links to another route — react-router navigation, not a hash jump. */
export const NavRouteLink = styled(LinkR)`
	${navLinkStyles}
`;

export const GitHubButton = styled.a`
	border: 1.8px solid var(--accent-interactive-text);
	justify-content: center;
	display: flex;
	align-items: center;
	height: 70%;
	border-radius: 20px;
	color: var(--accent-interactive-text);
	cursor: pointer;
	padding: 0 20px;
	font-weight: 500;
	text-decoration: none;
	font-size: 16px;
	white-space: nowrap;
	transition: all 0.6s ease-in-out;
	&:hover {
		background: var(--accent-interactive);
		color: var(--accent-interactive-contrast);
	}
	/* Follows the nav's own breakpoint (D-059, moved back to 768 in D-068).
	   Below it this component only ever renders inside the mobile menu, which
	   is where the smaller size is wanted. */
	@media screen and (max-width: 768px) {
		font-size: 14px;
	}
`;

export const ButtonContainer = styled.div`
	flex: 0 0 auto;
	height: 100%;
	display: flex;
	justify-content: end;
	align-items: center;
	gap: 8px;
	padding: 0 6px;
	/* Tracks NavItems' breakpoint (D-059, 768 again since D-068) — the desktop
	   chrome appears and disappears as one thing, or the toggle is stranded
	   next to a hamburger. */
	@media screen and (max-width: 768px) {
		display: none;
	}
`;

/**
 * A <button>, not a <div> (D-034). It carried the menu's click handler on a
 * bare <svg>: no role, no accessible name, and unreachable by keyboard —
 * WCAG 2.1.1 and 4.1.2, both level A, on the one control a keyboard user needs
 * to reach navigation at all below 768px. The reset declarations keep it
 * pixel-identical to the div it replaces.
 */
export const MobileIcon = styled.button`
	display: none;
	background: none;
	border: 0;
	padding: 0;
	/* Tracks NavItems' breakpoint (D-059, 768 again since D-068). */
	@media screen and (max-width: 768px) {
		display: block;
		position: absolute;
		top: 0;
		right: 0;
		transform: translate(-100%, 60%);
		font-size: 1.5rem;
		cursor: pointer;
		color: var(--text-primary);

		/* A 44px HIT AREA WITHOUT MOVING THE GLYPH (D-080). The button box was
		   exactly 24x24 — the icon and nothing else, because padding is 0 — which
		   is the smallest control on the site and also the primary way anyone
		   navigates it on a phone. It clears WCAG 2.2's 24px minimum (2.5.8) and
		   misses the 44px target size (2.5.5) by a factor of nearly two.

		   Padding would enlarge the box, and the box is positioned by
		   translate(-100%, 60%) — percentages OF ITS OWN SIZE — so growing it
		   moves the glyph. An inset ::after extends only the hit area: layout,
		   position and the rendered icon are byte-identical, and the extra 10px
		   on each side is reachable by touch and by mouse.

		   It follows that getBoundingClientRect() still reports 24px here, so the
		   audit verifies this by HIT-TESTING the corners with elementFromPoint
		   rather than by measuring the box. */
		&::after {
			content: "";
			position: absolute;
			inset: -10px;
		}
	}
`;

export const MobileMenu = styled.div<MobileMenuProps>`
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 16px;
	position: absolute;
	top: 80px;
	right: 0;
	width: 100%;
	padding: 12px 40px 24px 40px;
	/* THE GROUPED MENU IS TALLER THAN THE FLAT ONE (D-068): three headings
	   join the ten links, and on a 390x844 phone that is enough to push the
	   last destination past the bottom of a panel which is absolutely
	   positioned and therefore cannot be scrolled INTO. It scrolls itself now.
	   100dvh, not 100vh, because mobile Safari's 100vh is the
	   uncollapsed viewport — the one measurement guaranteed to be wrong while
	   the address bar is showing, which is exactly when someone opens the
	   navigation.

	   AND 128px, NOT 80px, WHICH IS THE PART THAT WAS WRONG FIRST. This panel
	   is absolutely positioned inside the sticky navbar, so its 80px offset is
	   measured from the NAVBAR's top — and at scroll position 0 the navbar is
	   not at the top of the viewport, the TopRibbon is. Measured at seven
	   widths from 320 to 768 in both themes, the ribbon is 35px at every one
	   of them, so the panel actually begins at 115px and a 80px subtraction
	   left 35px of it below the fold: reachable only by scrolling the page
	   until the sticky navbar pinned, which is not a thing a visitor knows to
	   do. 128px is the measured 115 rounded up to 8rem, so a later growth in
	   the ribbon cannot silently re-open the hole; over-subtracting only makes
	   the panel scroll internally a little sooner. */
	max-height: calc(100dvh - 128px);
	overflow-y: auto;
	overscroll-behavior: contain;
	background: var(--bg-elevated-translucent);
	/* The face is near-opaque in both themes now, so this blur is not what
	   makes the labels legible — the alpha is. It is here so that softening
	   that token again cannot silently put the page back behind the
	   navigation: whatever shows through is blurred, never readable. Same
	   vocabulary as the Experience panel, deliberately. */
	-webkit-backdrop-filter: blur(18px) saturate(150%);
	backdrop-filter: blur(18px) saturate(150%);
	transition: all 0.6s ease-in-out;
	transform: ${({ isOpen }) => (isOpen ? "translateY(0)" : "translateY(-100%)")};
	border-radius: 0 0 20px 20px;
	box-shadow: var(--shadow-menu);
	opacity: ${({ isOpen }) => (isOpen ? "100%" : "0")};
	z-index: ${({ isOpen }) => (isOpen ? "1000" : "-1000")};
`;

export const MobileLink = styled.a`
	${navLinkStyles}
`;

/** Mobile counterpart of NavRouteLink. */
export const MobileRouteLink = styled(LinkR)`
	${navLinkStyles}
`;

/* ==========================================================================
   THE GROUPED NAVIGATION (D-068)
   ==========================================================================

   Ten flat labels could not sit on one row at any width: D-066 measured the
   ninth wrapping the bar and D-067 measured the tenth joining the second row.
   Four can, at 769px, which is why the grouping is the fix and a smaller gap
   or a shorter label never was.

   THE PANEL IS OPAQUE, AND THAT IS AN ACCESSIBILITY DECISION RATHER THAN A
   VISUAL ONE (D-057). The open mobile menu spent months as a 0.6-alpha face
   over the hero photograph, and every contrast sweep reported the site clean
   because a ratio is computed against ONE ground colour and that ground was a
   photograph. A menu that opens over arbitrary page content must bring its own
   ground with it. --bg-elevated is solid in both themes, so the ink over it is
   a number a sweep can actually check.

   The face/shell pair is the D-061 card construct, unchanged: a 1px gradient
   hairline painted as the shell's background with the face inset over it. The
   a11y tree needs a UL for role=menu, so the gradient sits on a wrapper rather
   than on the list itself — same two elements as .card-shell / .card-face.

   These live in this file rather than in a new one: Constitution V.5 bars NEW
   styled-components files, and the alternative here was Tailwind classes on
   half of a component whose other half is styled-components, which is the
   drift the rule exists to prevent. */

/**
 * One <li> in NavItems. Every child of NavItems is one of these now, the plain
 * "About" link included: a UL whose children are bare anchors is invalid HTML,
 * and while no parser relocates them the way it would a stray table cell, this
 * document is pre-rendered and then re-parsed, so its validity is not a style
 * question here (D-042). The dropdown groups additionally need the positioning
 * context for their absolutely-placed panel.
 */
export const NavItem = styled.li`
	position: relative;
	display: flex;
	align-items: center;
`;

export const NavGroupTrigger = styled.button`
	${navLinkStyles}
	display: inline-flex;
	align-items: center;
	gap: 5px;
	background: none;
	border: 0;
	padding: 0;
	margin: 0;
	font: inherit;
	font-weight: 500;
	line-height: inherit;
	/* An open menu keeps its trigger lit, so the row always says which of the
	   three is showing. aria-expanded is the state, not a class — one source
	   of truth for the screen reader and the eye. */
	&[aria-expanded="true"] {
		color: var(--accent-interactive-text);
	}
	/* :focus-visible, not :focus. A mouse click on a <button> focuses it, and
	   a ring that appears on every click reads as a rendering fault; a
	   keyboard user must never be left guessing where they are. 2px of
	   --accent-interactive-text is 4.72:1 on the dark chrome and 7.10:1 on
	   porcelain, both clear of the 3:1 floor WCAG 1.4.11 sets for a non-text
	   indicator. */
	&:focus-visible {
		outline: 2px solid var(--accent-interactive-text);
		outline-offset: 3px;
		border-radius: 3px;
	}
`;

/**
 * A drawn chevron rather than the "▾" character. A glyph's width and baseline
 * come from whichever font actually resolved, so a text arrow moves the label
 * beside it by an amount no width sweep can predict; this one is 10px in every
 * font on every platform.
 */
export const NavChevron = styled.svg`
	width: 10px;
	height: 10px;
	flex: 0 0 auto;
	transition: transform 0.2s ease-in-out;
	&[data-open="true"] {
		transform: rotate(180deg);
	}
`;

export const NavMenuShell = styled.div`
	position: absolute;
	top: calc(100% + 14px);
	left: 50%;
	transform: translateX(-50%);
	border-radius: 14px;
	padding: 1px;
	background-image: linear-gradient(
		to bottom right,
		var(--grad-border-from),
		var(--grad-border-via),
		var(--grad-border-to)
	);
	box-shadow: var(--shadow-menu);
	z-index: 40;
	/* display:none is what keeps a closed menu out of the tab order AND out of
	   the accessibility tree — visibility or opacity would leave ten links
	   focusable behind a closed control, which is the keyboard-trap shape this
	   component exists to avoid. It is also why the markup can be rendered
	   unconditionally: the links stay in the pre-rendered HTML for a crawler,
	   and no one can Tab into them. */
	display: none;
	&[data-open="true"] {
		display: block;
	}
`;

export const NavMenuFace = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 190px;
	margin: 0;
	padding: 6px;
	list-style: none;
	border-radius: 13px;
	border: 1px solid var(--border);
	background-color: var(--bg-elevated);
`;

/**
 * Hover and focus move the FILL, not the ink.
 *
 * The obvious styling — tint the label with --accent-interactive-text on
 * hover, as every other link on this site does — fails AA here and the
 * arithmetic is worth keeping. --tile-fill-hover is a 16% white veil, so on
 * the dark chrome it lifts the ground from #191924 to rgb(62,62,71); the
 * accent measures 2.87:1 against that, under the 4.5 floor. The accent is
 * only ever safe on the UNVEILED chrome (4.72:1), which is where the trigger
 * above uses it. So the row lights up and the text goes to --text-strong,
 * which is 10.6:1 on the veiled ground in dark and higher in light.
 */
export const NavMenuItemLink = styled.a`
	display: block;
	padding: 8px 12px;
	border-radius: 8px;
	color: var(--text-primary);
	font-size: 0.95rem;
	font-weight: 500;
	text-decoration: none;
	white-space: nowrap;
	transition:
		background-color 0.18s ease-in-out,
		color 0.18s ease-in-out;
	&:hover {
		background-color: var(--tile-fill-hover);
		color: var(--text-strong);
	}
	/* :focus, not :focus-visible. Arrow-key movement inside a role=menu is
	   programmatic .focus(), and Chrome does not always match :focus-visible
	   on a programmatically focused element — so the one user who can ONLY
	   navigate this menu with arrows is the one who would lose the indicator. */
	&:focus {
		background-color: var(--tile-fill-hover);
		color: var(--text-strong);
		outline: 2px solid var(--accent-interactive-text);
		outline-offset: -2px;
	}
`;

/**
 * The mobile menu's group label. A heading, not a control: the phone menu
 * shows every destination at once, so a second disclosure layer would put two
 * taps between a thumb and a link to save vertical space the panel can scroll
 * through anyway.
 */
export const MobileGroupHeading = styled.p`
	margin: 8px 0 0 0;
	font-size: 12px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-muted);
`;
